/** Which case types a playbook rule is about; none picked means every case. */
import { Check, ChevronDown, X } from "lucide-react";
import { useState, type FC } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { EMPLOYMENT_BASED_VISAS, VISAVALUE } from "@/types/visa.type";

const ALL = Object.values(VISAVALUE).filter((v) => v !== VISAVALUE.EMPTY);
const EMPLOYMENT = ALL.filter((v) => EMPLOYMENT_BASED_VISAS.has(v));
const OTHER = ALL.filter((v) => !EMPLOYMENT_BASED_VISAS.has(v));

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  /** Case types the company has cases of; listed first. */
  common?: string[];
  disabled?: boolean;
};

export const CaseTypePicker: FC<Props> = ({
  value,
  onChange,
  common = [],
  disabled
}) => {
  const [open, setOpen] = useState(false);
  const toggle = (v: string) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const known = new Set<string>(ALL);
  const yours = common.filter((v) => known.has(v));
  const groups: Array<[string, string[]]> = [
    ["This company's cases", yours],
    ["Employment-based", EMPLOYMENT.filter((v) => !yours.includes(v))],
    ["Other", OTHER.filter((v) => !yours.includes(v))]
  ];

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {value.map((v) => (
        <Badge key={v} variant="secondary" className="gap-1 font-normal">
          {v}
          {disabled ? null : (
            <button
              type="button"
              aria-label={`Remove ${v}`}
              className="text-fg-3 hover:text-fg"
              onClick={() => toggle(v)}
            >
              <X className="size-3" />
            </button>
          )}
        </Badge>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="xs"
            variant="secondary"
            disabled={disabled}
            aria-label="Case types"
          >
            {value.length ? "Change" : "All cases"} <ChevronDown />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command>
            <CommandInput placeholder="Search case types" />
            <CommandList className="max-h-64">
              <CommandEmpty>No case type found.</CommandEmpty>
              <CommandItem
                value="all cases"
                onSelect={() => onChange([])}
                className="text-sm"
              >
                <Check
                  className={cn("size-3.5", value.length && "opacity-0")}
                />
                All cases
              </CommandItem>
              {groups.map(([heading, items]) =>
                items.length ? (
                  <CommandGroup key={heading} heading={heading}>
                    {items.map((v) => (
                      <CommandItem
                        key={v}
                        value={v}
                        onSelect={() => toggle(v)}
                        className="text-sm"
                      >
                        <Check
                          className={cn(
                            "size-3.5",
                            !value.includes(v) && "opacity-0"
                          )}
                        />
                        {v}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </span>
  );
};
